import { useEffect, useId, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import Button from '../../components/ui/Button'
import ConfirmDialog from '../../components/ui/ConfirmDialog'
import { confirmDialog } from 'primereact/confirmdialog'
import FormDialog from '../../components/ui/FormDialog'
import FormField from '../../components/ui/FormField'
import MobileSheetAutoComplete from '../../components/ui/MobileSheetAutoComplete'
import { useToast } from '../../components/ui/ToastProvider'
import { useCurrentUser } from '../../auth/useCurrentUser'
import { Dropdown } from 'primereact/dropdown'
import { formatDate } from '../../lib/datetime'
import { getApiErrorCode } from '../../api/apiError'
import { notifyApiError } from '../../api/errorNotifier'
import {
  assistantKeys,
  getAssistantState,
  removeAssistant,
  searchAssistantCandidates,
  upsertAssistant,
} from './api/assistantApi'
import { useEmployeeSearch, type EmployeeSearchSource } from '../minutes/hooks/useEmployeeSearch'
import type { EmployeePick } from '../minutes/types'
import '../minutes/minutes.css'
import './assistant.css'

/**
 * Asistan adayı arama kaynağı — **kıdem süzgeci uygulamayan** kendi ucumuz, ayrı önbellek anahtarı.
 *
 * <p>125'in Ajanda araması da aynı yoldan geçiyor: {@link useEmployeeSearch} kaynak parametresi
 * alacak biçimde tasarlanmış, bu yüzden arama mantığı (debounce, yarış koruması, taze referansla
 * panel açma) yeniden yazılmıyor. Değişen tek şey hangi ucun çağrıldığı.
 */
/**
 * Onay penceresinin yayın grubu (JSDoc bloğu 17.10'da yanlış sabitin üstündeydi, düzeltildi). Gruplandırma **zorunlu**: grupsuz bir {@code confirmDialog()} çağrısı
 * açık olan her grupsuz örneğe yayın yapar, yani aynı anda birden çok onay penceresi açılırdı
 * ({@code NotificationsButton} aynı tuzağı adıyla kaydediyor).
 */
const REMOVE_CONFIRM_GROUP = 'assistant-remove'

/**
 * Kaydetmenin onay penceresi — kaldırmadan **ayrı grup**, çünkü iki pencere aynı ekranda yaşıyor ve
 * grup paylaşsalardı bir eylemin onayı diğerinin örneğine de yayın yapardı (yukarıdaki not).
 */
const SAVE_CONFIRM_GROUP = 'assistant-save'

/**
 * Kaydetmenin **onay ânında donan** yükü: hem bildirimin metni hem de sunucuya gidecek değerler.
 *
 * <p><b>Sicil ve yetki de buraya dahildir, ve bu zorunludur.</b> İlk yazımda yalnız metin
 * donduruluyor, yük ise {@code mutationFn}'in canlı kapanışından okunuyordu; TanStack seçenekleri her
 * render'da yeniden bağladığı için {@code mutate()} çağrıldığında <b>en son render'ın</b> değerleri
 * gidiyordu. Ölçülen sonuç (push kapısı review'ü): kullanıcı "Okan / Tam Yetki" onaylarken sunucuya
 * başka bir sicil ve Kısıtlı Yetki gitti, bildirim de Okan'ın yetkisinin güncellendiğini söyledi —
 * üçü birbirini tutmuyordu. Tetikleyici gerçek: bu ekranın sorgusu {@code staleTime} taşımıyor,
 * pencere odağı değişince tazeleniyor ve {@code useEffect} seçimi state'ten yeniden kuruyor.
 *
 * <p>Onayın tüm anlamı "onayladığın şey gönderilir"dir; o yüzden yükün tamamı tek yerde donar.
 */
interface SaveOutcome {
  kind: 'assign' | 'replace' | 'authority'
  name: string
  employeeId: string
  authorityId: number
}

const ASSISTANT_SOURCE: EmployeeSearchSource = {
  fetch: (q) => searchAssistantCandidates(q),
  cacheKey: 'assistant-candidate-search',
}

/**
 * Aday listesindeki tek satır — **modül düzeyinde**, {@code AssistantDialog}'un gövdesinde değil.
 *
 * <p>PrimeReact bunu bir bileşen olarak değil, {@code itemTemplate} olarak **çağırır**; yani JSX'in
 * içine gömülüyken de ağaç her render'da sökülmüyordu. Modül düzeyinde durmasının sebebi Sonar
 * S6478'dir — performans değil. {@code AssistantDialog}'un kendi javadoc'u export'un hemen üstünde
 * kalsın diye bildirim buraya, {@code interface Props}'un önüne kondu.
 */
function candidateItem(item: EmployeePick) {
  return (
    <div className="minutes-emp-item">
      <span className="minutes-emp-name">{item.fullName}</span>
      {item.jobTitle && <span className="minutes-emp-title">{item.jobTitle}</span>}
      <span className="minutes-emp-sicil">{item.employeeId}</span>
    </div>
  )
}

interface Props {
  visible: boolean
  onHide: () => void
}

/**
 * "Asistan Tanımla" pop-up'ı (126) — kimlik menüsünden açılır.
 *
 * <p>Ekran üç şeyi bir arada gösterir: mevcut asistan (varsa), yeni kişi seçimi ve yetki seviyesi.
 * Atama ile değiştirme <b>aynı</b> formdur, çünkü sunucu tarafında da aynı işlemdir (tekil alt kaynak
 * → upsert): kullanıcı için "değiştir" diye ayrı bir akış kurmak, aynı şeyi iki kez anlatmak olurdu.
 *
 * <p><b>İptal butonu yoktur</b> ve bu uygulama geneli bir karardır ({@link FormDialog}): vazgeçme
 * başlıktaki ×, maske tıklaması ya da Esc ile olur. "Asistanı Kaldır" bu yüzden footer'a değil
 * gövdeye, mevcut asistanın yanına kondu — o bir vazgeçme değil, yıkıcı bir eylemdir ve onay ister.
 */
export default function AssistantDialog({ visible, onHide }: Readonly<Props>) {
  const { t } = useTranslation()
  const toast = useToast()
  const queryClient = useQueryClient()
  // Önbellek anahtarı kimliğe bağlı (bkz. `assistantKeys.state`): persona değişince bu diyalog da
  // önceki kimliğin asistanını göstermemeli.
  const { data: me } = useCurrentUser()
  const inputId = useId()
  const authorityId_ = useId()

  const { suggestions, search } = useEmployeeSearch(ASSISTANT_SOURCE)
  const [query, setQuery] = useState('')
  const [picked, setPicked] = useState<EmployeePick | null>(null)
  const [authorityId, setAuthorityId] = useState<number | null>(null)
  const [authorityTouched, setAuthorityTouched] = useState(false)

  const { data: state } = useQuery({
    queryKey: assistantKeys.state(me?.userId),
    enabled: visible,
    queryFn: getAssistantState,
  })

  // Pop-up her açılışta temiz başlar; önceki seçim ekranda kalsaydı kullanıcı onu kaydedilmiş sanabilirdi.
  useEffect(() => {
    if (visible) {
      setQuery('')
      setPicked(null)
      setAuthorityId(state?.assistant?.authorityId ?? null)
      setAuthorityTouched(false)
    }
  }, [visible, state?.assistant?.authorityId])

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: assistantKeys.state(me?.userId) })

  const current = state?.assistant ?? null
  // Değiştirmek için yeni kişi seçmek şart değil: aynı kişinin yalnız yetkisi de güncellenebilir.
  const targetId = picked?.employeeId ?? current?.employeeId ?? null

  /**
   * Kaydetmenin **üç ayrı sonucu** var ve kullanıcıya hangisinin olacağı söylenmeli:
   *
   * - `assign` — tanımlı asistan yokken yeni atama.
   * - `replace` — mevcut asistanın yerine **başka** biri. Bunun ayrı durulmasının sebebi somut:
   *   sunucu eski atamayı pasife çeker, yani o kişinin erişimi **sessizce kalkar**. Onayın asıl
   *   gerekçesi budur; tek bir "kaydedilsin mi?" sorusu bu sonucu gizlerdi.
   * - `authority` — aynı kişi, yalnız yetki seviyesi değişiyor (FR-009).
   *
   * Sicil karşılaştırması `trim` ile yapılır: iki değer de İK'dan geliyor ve dolgulu, ama kolon sabit
   * genişlikli olduğu için sondaki boşluk eşleşmeyi sessizce bozabilir.
   */
  const sameSicil = (a?: string | null, b?: string | null) =>
    a != null && b != null && a.trim() === b.trim()
  let saveKind: SaveOutcome['kind'] = 'assign'
  if (current) {
    saveKind = picked && !sameSicil(picked.employeeId, current.employeeId) ? 'replace' : 'authority'
  }

  const targetName = picked?.fullName ?? current?.fullName ?? ''
  // Yetki etiketi KATALOGDAN okunur, 40/41 gömülmez (research D7) — onay metni de aynı kurala uyar.
  const authorityLabel =
    state?.authorities?.find((option) => option.id === authorityId)?.name ?? ''

  const save = useMutation({
    // Yük DEĞİŞKENDEN okunur, kapanıştan DEĞİL — gerekçe `SaveOutcome`'ın javadoc'unda ölçülmüş
    // haliyle duruyor. (Sicilin `picked` değil `targetId`'den türemesi ayrı bir karar: yeni kişi
    // seçmeden yalnız yetki seviyesini değiştirmek meşru bir akış (FR-009) ve orada `picked` null
    // olur; ilk yazımda gövdeye boş sicil gidip sunucu 400 dönüyordu.)
    mutationFn: (outcome: SaveOutcome) => upsertAssistant(outcome.employeeId, outcome.authorityId),
    // Bildirimin metni MUTASYON DEĞİŞKENİNDEN okunur, kapanıştan değil: onay penceresi açıkken
    // `state` tazelenirse (ya da `invalidate()` sonrası yeniden render olursa) kapanıştaki `current`
    // değişmiş olabilir ve bildirim yanlış adı/sonucu gösterirdi. Değişken, kullanıcının ONAYLADIĞI
    // ânın bilgisini taşır — doğru olan da odur.
    onSuccess: async (_data, outcome) => {
      await invalidate()
      // Başarı bildirimi verilir: pop-up'ın kapanması tek görünür geri bildirim olsaydı kullanıcı
      // "kaydedildi mi, yoksa pencere mi kapandı?" sorusuyla kalırdı (kullanıcı bildirimi 20.10.2026).
      // Metin hangi sonucun olduğunu söyler — tek bir "kaydedildi" üç durumu da aynı gösterirdi.
      toast.success(t(`assistant.toast.${outcome.kind}`, { name: outcome.name }))
      onHide()
    },
    onError: (error) => {
      // Sunucunun alan bazlı reddi kullanıcıya anlaşılır biçimde dönsün; kalanı ortak bildirici.
      if (getApiErrorCode(error) === 'ASSISTANT_NOT_ELIGIBLE') {
        toast.error(t('assistant.error.notEligible'))
        return
      }
      // KOD'a bakılır, alana DEĞİL: `employeeId` alanı üç ayrı sebebi taşıyor (biçim, İK'da aktif
      // karşılığı yok, kendini atama). Alana bakan ilk sürüm, arama ile kaydet arasında pasifleşen
      // bir aday seçildiğinde kullanıcıya "kendinizi seçemezsiniz" gösteriyordu (review turu 2).
      if (getApiErrorCode(error) === 'ASSISTANT_SELF_NOT_ALLOWED') {
        toast.error(t('assistant.error.selfNotAllowed'))
        return
      }
      notifyApiError(error)
    },
  })

  // Jenerikle tiplenir (emsal: `useNotifications`), parametre YAZILMAZ: `mutationFn`'in kullanmadığı
  // bir parametre `@typescript-eslint/no-unused-vars` hatası verir ve `npm run lint` Jenkins'te
  // testlerden ÖNCE koştuğu için build'i hiç başlatmadan keser (push kapısı review'ünde ölçüldü).
  const drop = useMutation<void, unknown, string>({
    mutationFn: () => removeAssistant(),
    // Ad yine değişkenden gelir: `invalidate()` sonrası `current` null'a düşer, kapanıştan okunsaydı
    // bildirim adsız kalırdı. Kaldırılan kişinin adı bu yıkıcı eylemin en önemli geri bildirimidir —
    // yanlış kişiyi kaldırdığını kullanıcı ancak oradan anlar.
    onSuccess: async (_data, removedName) => {
      await invalidate()
      toast.success(t('assistant.toast.removed', { name: removedName }))
      onHide()
    },
    // Referansı DOĞRUDAN geçmek derlenmiyor: `notifyApiError`'ın ikinci parametresi ({ meta })
    // mutasyonun değişken tipini kendine çekiyor ve `mutate(string)` reddediliyordu. Sarmalayıcı
    // niyeti de netleştiriyor — bildiriciye yalnız hata gider, mutasyonun değişkeni değil.
    onError: (error) => notifyApiError(error),
  })

  const canSubmit = targetId != null && authorityId != null
  const authorityError =
    authorityTouched && authorityId == null ? t('assistant.authority.required') : undefined

  return (
    <FormDialog
      visible={visible}
      header={t('assistant.dialog.title')}
      submitLabel={t('assistant.actions.save')}
      submitting={save.isPending}
      submitDisabled={!canSubmit}
      onSubmit={() => {
        setAuthorityTouched(true)
        if (!canSubmit || targetId == null || authorityId == null) return
        // Onay ZORUNLU: bu form birinin yöneticinin verisine erişimini açar, değiştirir ya da
        // (yerine başkası atanınca) sessizce kapatır. Kaydet'e yanlışlıkla basmanın bedeli,
        // onaylatmanın maliyetinden büyük. Metin hangi sonucun olacağını adıyla söyler.
        confirmDialog({
          group: SAVE_CONFIRM_GROUP,
          header: t('assistant.save.confirmHeader'),
          message: t(`assistant.save.confirm.${saveKind}`, {
            name: targetName,
            current: current?.fullName ?? '',
            authority: authorityLabel,
          }),
          acceptLabel: t('assistant.actions.save'),
          rejectLabel: t('common.cancel'),
          accept: () =>
            save.mutate({ kind: saveKind, name: targetName, employeeId: targetId, authorityId }),
        })
      }}
      onHide={onHide}
    >
      <section className="assistant-current">
        <h3>{t('assistant.current.title')}</h3>
        {current ? (
          <div className="assistant-card">
            {/* Ad ve yikici eylem aynı satirda, iki uca yaslı: ad soldan okunur, "Kaldır" hep
                aynı yerde durur. Oncesinde ikisi de akan bir satirdaydi ve kart tek bir cumle
                gibi okunuyordu (kullanici bildirimi 19.10.2026). */}
            <div className="assistant-card-head">
              <span className="assistant-name">{current.fullName}</span>
              {/* type="button" ZORUNLU: buton FormDialog'un <form>'u icinde ve tipsiz buton gonder
                  sayilir — tik formu da gonderip kaldirma onayinin USTUNE kaydetme onayini aciyordu
                  (kullanici bildirimi 21.10.2026). */}
              <Button
                type="button"
                label={t('assistant.actions.remove')}
                aria-label={t('assistant.actions.removeNamed', { name: current.fullName })}
                severity="danger"
                text
                onClick={() =>
                  confirmDialog({
                    group: REMOVE_CONFIRM_GROUP,
                    header: t('assistant.actions.remove'),
                    message: t('assistant.remove.confirm', { name: current.fullName }),
                    acceptLabel: t('assistant.actions.remove'),
                    rejectLabel: t('common.cancel'),
                    accept: () => drop.mutate(current.fullName),
                  })
                }
              />
            </div>

            {current.jobTitle && <span className="assistant-meta">{current.jobTitle}</span>}

            <div className="assistant-card-foot">
              {/* Yetki seviyesi ROZET: FR-015 bu ayrimin gizlenmemesini istiyor, akan metinde
                  diger alanlarla esit agirliktaydi. Etiket KATALOGDAN gelir (authorityName);
                  40/41 frontend'e GOMULMEZ - research D7. Bu yuzden ton da sabit (notr):
                  renk ayrimi yapmak kimlige bakmayi gerektirirdi. */}
              {current.authorityName && (
                <span className="ui-badge ui-badge-soft t-neutral">{current.authorityName}</span>
              )}
              {current.assignmentDate && (
                <span className="assistant-meta">
                  {t('assistant.current.assignedOn', { date: formatDate(current.assignmentDate) })}
                </span>
              )}
            </div>
          </div>
        ) : (
          <p className="assistant-empty">{t('assistant.current.none')}</p>
        )}
      </section>

      <FormField label={t('assistant.search.label')} htmlFor={inputId}>
        <MobileSheetAutoComplete<EmployeePick>
          value={query}
          suggestions={suggestions}
          completeMethod={(e) => search(e.query)}
          field="fullName"
          delay={300}
          minLength={2}
          placeholder={t('assistant.search.placeholder')}
          inputId={inputId}
          className="minutes-participant-input"
          panelClassName="minutes-participant-panel"
          onChange={(e) => {
            setQuery(typeof e.value === 'string' ? e.value : ((e.value as EmployeePick)?.fullName ?? ''))
            if (typeof e.value === 'string') setPicked(null)
          }}
          onSelect={(e) => {
            setPicked(e.value as EmployeePick)
            setQuery((e.value as EmployeePick).fullName)
          }}
          itemTemplate={candidateItem}
        />
      </FormField>

      {/* Sarmalayici YALNIZ bosluk icindir: FormField className almiyor ve paylasilan bilesenin
          API'sini tek bir ekran icin genisletmek istemedik. */}
      <div className="assistant-authority-field">
        <FormField
          label={t('assistant.authority.label')}
          htmlFor={authorityId_}
          required
          error={authorityError}
        >
          <Dropdown
            inputId={authorityId_}
            value={authorityId}
            options={state?.authorities ?? []}
            optionLabel="name"
            optionValue="id"
            className={authorityError ? 'p-invalid' : undefined}
            onChange={(e) => {
              setAuthorityTouched(true)
              setAuthorityId(e.value as number)
            }}
          />
        </FormField>
      </div>

      {/* Örnek FormDialog'un İÇİNDE durur — `TodoShareDialog` / `PrincipleFilesDialog` ile aynı
          kalıp. Dışarıda, kardeş olarak render edildiğinde onay penceresi açılınca arkadaki
          pop-up tarayıcıda kayboluyordu (kullanıcı bildirimi 19.10.2026). Aynı kabuğu (ve aynı
          `dismissableMask` ayarını) kullanan o iki ekranda sorun yok; tek fark buydu.
          NOT: jsdom bu farkı yeniden üretmiyor, yani `src/test/AssistantDialog.test.tsx` kaybolmayı YAKALAMAZ —
          doğrulama tarayıcıda yapıldı. */}
    <ConfirmDialog group={REMOVE_CONFIRM_GROUP} />
    <ConfirmDialog group={SAVE_CONFIRM_GROUP} />
    </FormDialog>
  )
}
